import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminSubpage from "../components/AdminSubpage";
import { auctionsAPI } from "../services/api";
import "../components/Auctions.css";

const AuctionBidsPage = () => {
  const { auctionId } = useParams();
  const [auction, setAuction] = useState(null);
  const [items, setItems] = useState([]);
  const [bids, setBids] = useState([]);
  const [itemFilter, setItemFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = itemFilter ? { item_id: itemFilter } : {};
      const res = await auctionsAPI.getBids(auctionId, params);
      setAuction(res.data.auction);
      setItems(res.data.items || []);
      setBids(res.data.bids || []);
    } catch {
      toast.error("Failed to load bids");
    } finally {
      setLoading(false);
    }
  }, [auctionId, itemFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    const rows = [["Time", "Item", "Bidder", "Email", "Amount (AED)", "Winning"]];
    bids.forEach((b) => {
      const name = b.user
        ? `${b.user.first_name || ""} ${b.user.last_name || ""}`.trim()
        : b.bidder_name || "";
      rows.push([
        b.created_at ? new Date(b.created_at).toLocaleString() : "",
        b.item_title || "",
        name,
        b.user?.email || "",
        b.amount,
        b.is_winning ? "Yes" : "No",
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `auction-${auctionId}-bids.csv`;
    a.click();
  };

  if (loading && !auction) {
    return (
      <AdminSubpage title="Auction bids" backTo="/auctions">
        <div className="loading">Loading…</div>
      </AdminSubpage>
    );
  }

  return (
    <AdminSubpage
      title={auction ? `Bids — ${auction.title}` : "Auction bids"}
      backTo="/auctions"
    >
      <div className="admin-page-panel modal-content">
        <div className="auctions-toolbar" style={{ marginBottom: 16 }}>
          <select
            value={itemFilter}
            onChange={(e) => setItemFilter(e.target.value)}
          >
            <option value="">All items</option>
            {items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title} ({item.status})
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-secondary" onClick={exportCsv}>
            Export CSV
          </button>
          <Link to={`/auctions/${auctionId}/live`} className="btn btn-secondary">
            Live control
          </Link>
        </div>

        <div className="auction-live-stats">
          <div className="auction-live-stat">
            <label>Total bids</label>
            <strong>{bids.length}</strong>
          </div>
          <div className="auction-live-stat">
            <label>Items</label>
            <strong>{items.length}</strong>
          </div>
        </div>

        <div className="table-container" style={{ marginTop: 16 }}>
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Item</th>
                <th>Bidder</th>
                <th>Email</th>
                <th>Amount</th>
                <th>Winning</th>
              </tr>
            </thead>
            <tbody>
              {bids.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", color: "var(--text-muted)" }}>
                    No bids yet.
                  </td>
                </tr>
              ) : null}
              {bids.map((b) => {
                const name = b.user
                  ? `${b.user.first_name || ""} ${b.user.last_name || ""}`.trim()
                  : b.bidder_name || "—";

                return (
                  <tr key={b.id}>
                    <td>
                      {b.created_at
                        ? new Date(b.created_at).toLocaleString()
                        : "—"}
                    </td>
                    <td>{b.item_title || "—"}</td>
                    <td>{name || "—"}</td>
                    <td>{b.user?.email || "—"}</td>
                    <td>AED {Number(b.amount).toLocaleString()}</td>
                    <td>{b.is_winning ? "Yes" : "No"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminSubpage>
  );
};

export default AuctionBidsPage;
